import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { DetailPanel } from '../components/flowDebugger/DetailPanel'
import { MetricsBadge } from '../components/flowDebugger/MetricsBadge'
import { StepNode } from '../components/flowDebugger/StepNode'
import type { Conversation, Tenant, TraceStep, TraceSummary } from '../components/flowDebugger/types'
import { buildTraceDebugBundle } from '../components/flowDebugger/constants'
import { traceService, type HitlResumePayload, type TestChatEvaluationResult } from '../services/traceService'
import DebugTraceAnalysisPanel from './DebugTraceAnalysisPanel'
import { Bot, Play } from 'lucide-react'
import TestChatEvaluationModal from '../../components/bot/TestChatEvaluationModal'

type HitlUiAction = 'approve' | 'edit' | 'deny'

const HITL_PAUSE_STEP_TYPES = new Set(['GRAPH_HITL_INTERRUPTED', 'HITL_INTERRUPT_TRIGGERED'])
const HITL_RESUME_STEP_TYPES = new Set(['HITL_INTERRUPT_RESUMED', 'HITL_RESUME'])

function getHitlPendingState(steps: TraceStep[]) {
  let lastPauseIndex = -1
  let lastResumeIndex = -1
  let latestRequest: Record<string, unknown> | null = null

  steps.forEach((step, index) => {
    if (HITL_PAUSE_STEP_TYPES.has(step.type)) {
      lastPauseIndex = index
      const request = step.type === 'GRAPH_HITL_INTERRUPTED' ? step.data?.hitlRequest : step.data
      latestRequest = request && typeof request === 'object' ? (request as Record<string, unknown>) : null
    }

    if (HITL_RESUME_STEP_TYPES.has(step.type)) {
      lastResumeIndex = index
    }
  })

  return {
    pending: lastPauseIndex >= 0 && lastPauseIndex > lastResumeIndex,
    request: latestRequest,
  }
}

export default function ConversationFlowDebugger() {
  const [searchParams] = useSearchParams()
  const initialTenantId = searchParams.get('tenantId') || ''
  const initialConversationId = searchParams.get('conversationId') || ''

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [tenantId, setTenantId] = useState(initialTenantId)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [conversationId, setConversationId] = useState(initialConversationId)
  const [trace, setTrace] = useState<TraceStep[]>([])
  const [historyTraces, setHistoryTraces] = useState<TraceSummary[]>([])
  const [activeTraceId, setActiveTraceId] = useState<string | null>(null)
  const [active, setActive] = useState<number | null>(null)
  const [filter, setFilter] = useState('ALL')
  const [liveMode, setLiveMode] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingTrace, setLoadingTrace] = useState(false)
  const [copyStatus, setCopyStatus] = useState('')
  const [error, setError] = useState('')
  const [hitlAction, setHitlAction] = useState<HitlUiAction>('approve')
  const [hitlEditedMessage, setHitlEditedMessage] = useState('')
  const [hitlReason, setHitlReason] = useState('')
  const [hitlSubmitting, setHitlSubmitting] = useState(false)
  const [hitlError, setHitlError] = useState('')
  const [hitlStatus, setHitlStatus] = useState('')
  const [hitlPendingResponse, setHitlPendingResponse] = useState('')
  const [playing, setPlaying] = useState(false)
  const [visibleIndexes, setVisibleIndexes] = useState<number[]>([])
  const [playhead, setPlayhead] = useState(0)
  const liveCleanupRef = useRef<null | (() => void)>(null)
  const playbackRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [isAuditing, setIsAuditing] = useState(false)
  const [auditResult, setAuditResult] = useState<TestChatEvaluationResult | null>(null)
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false)
  const [auditError, setAuditError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const loadTenants = async () => {
      setLoading(true)
      setError('')
      try {
        const response = await traceService.getTenants()
        if (cancelled) return
        setTenants(response.data)
        setTenantId((current) => current || response.data[0]?.id || '')
      } catch {
        if (!cancelled) setError('No se pudieron cargar los tenants')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void loadTenants()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!tenantId) {
      setConversations([])
      setConversationId('')
      return
    }

    let cancelled = false
    const loadConversations = async () => {
      setError('')
      try {
        const response = await traceService.getConversations(tenantId)
        if (cancelled) return
        setConversations(response.data)
        setConversationId((current) => {
          if (current && response.data.some((item) => item.id === current)) return current
          return response.data[0]?.id || ''
        })
      } catch {
        if (!cancelled) {
          setConversations([])
          setConversationId('')
          setError('No se pudieron cargar las conversaciones')
        }
      }
    }
    void loadConversations()
    return () => {
      cancelled = true
    }
  }, [tenantId])

  useEffect(() => {
    setPlaying(false)
    setVisibleIndexes([])
    setPlayhead(0)
    setLoadingTrace(false)
    setError('')
    setHistoryTraces([])
    setActiveTraceId(null)

    if (!conversationId) {
      setTrace([])
      setActive(null)
      return
    }

    let cancelled = false
    const loadTraceHistory = async () => {
      setLoadingTrace(true)
      setError('')
      try {
        const traceList = await traceService.getTracesByConversation(conversationId, { tenantId, limit: 40, page: 1 })
        if (cancelled) return
        
        setHistoryTraces(traceList.data || [])
        const latest = traceList.data[0]
        
        if (!latest) {
          setTrace([])
          setActive(null)
          if (liveMode) {
            liveCleanupRef.current = traceService.subscribeToLiveTrace(conversationId, tenantId, (step) => {
              setTrace((current) => [...current, step])
            })
          }
          return
        }

        setActiveTraceId(latest.id)
        const detail = await traceService.getTraceDetailForTenant(latest.id, tenantId)
        if (cancelled) return
        setTrace(detail.steps || [])
        setActive((detail.steps || []).length > 0 ? (detail.steps || []).length - 1 : null)
        setVisibleIndexes((detail.steps || []).map((_, index) => index))
        setPlayhead((detail.steps || []).length)
        
        if (liveMode) {
          liveCleanupRef.current = traceService.subscribeToLiveTrace(conversationId, tenantId, (step) => {
            setTrace((current) => [...current, step])
          })
        }
      } catch {
        if (!cancelled) {
          setTrace([])
          setActive(null)
          setError('No se pudo cargar el historial de traces')
        }
      } finally {
        if (!cancelled) {
          setLoadingTrace(false)
        }
      }
    }
    void loadTraceHistory()
    return () => {
      cancelled = true
    }
  }, [conversationId, liveMode, tenantId])

  // Effect to load a specific trace from history
  useEffect(() => {
    if (!activeTraceId || liveMode) return
    
    // If it's the latest one we already loaded it in the conversation effect, 
    // but for simplicity and to handle manual selections, we can re-fetch or find it.
    // However, the initial load already sets it. If the ID changes, we fetch.
    
    let cancelled = false
    const loadSpecificTrace = async () => {
      setLoadingTrace(true)
      try {
        const detail = await traceService.getTraceDetailForTenant(activeTraceId, tenantId)
        if (cancelled) return
        setTrace(detail.steps || [])
        setActive((detail.steps || []).length > 0 ? (detail.steps || []).length - 1 : null)
        setVisibleIndexes((detail.steps || []).map((_, index) => index))
        setPlayhead((detail.steps || []).length)
      } catch {
        if (!cancelled) setError('No se pudo cargar el detalle del trace')
      } finally {
        if (!cancelled) setLoadingTrace(false)
      }
    }

    // Skip if we are pointing to the already loaded latest trace to avoid double fetch on start
    const isLatest = historyTraces.length > 0 && historyTraces[0].id === activeTraceId
    if (!isLatest || trace.length === 0) {
      void loadSpecificTrace()
    }

    return () => {
      cancelled = true
    }
  }, [activeTraceId])

  useEffect(() => {
    if (liveMode) {
      setVisibleIndexes(trace.map((_, index) => index))
      setActive(trace.length > 0 ? trace.length - 1 : null)
      return
    }
    if (trace.length === 0) {
      setVisibleIndexes([])
      setPlayhead(0)
    }
  }, [trace, liveMode])

  useEffect(() => {
    if (!playing || liveMode) return
    if (playhead >= trace.length) {
      setPlaying(false)
      return
    }

    const delay = playhead === 0 ? 120 : Math.min(Math.max((trace[playhead]?.ms || 0) - (trace[playhead - 1]?.ms || 0), 120), 900)
    playbackRef.current = setTimeout(() => {
      setVisibleIndexes((current) => (current.includes(playhead) ? current : [...current, playhead]))
      setActive(playhead)
      setPlayhead((current) => current + 1)
    }, delay)

    return () => {
      if (playbackRef.current) clearTimeout(playbackRef.current)
    }
  }, [playing, playhead, trace, liveMode])

  useEffect(() => {
    return () => {
      if (liveCleanupRef.current) liveCleanupRef.current()
      if (playbackRef.current) clearTimeout(playbackRef.current)
    }
  }, [])

  useEffect(() => {
    setHitlAction('approve')
    setHitlEditedMessage('')
    setHitlReason('')
    setHitlError('')
    setHitlStatus('')
    setHitlPendingResponse('')
  }, [conversationId, activeTraceId])

  const filtered = useMemo(
    () =>
      trace.filter((step) =>
        filter === 'ALL'
          ? true
          : filter === 'TOOLS'
            ? ['TOOL_CALL', 'TOOL_EXECUTION', 'CACHE'].includes(step.type)
            : filter === 'LLM'
              ? step.type === 'LLM'
              : filter === 'ERRORS'
                ? step.type === 'ERROR'
                : true
      ),
    [trace, filter]
  )

  const llmCalls = trace.filter((step) => step.type === 'LLM').length
  const toolCalls = trace.filter((step) => ['TOOL_CALL', 'TOOL_EXECUTION'].includes(step.type)).length
  const cacheHits = trace.filter((step) => step.type === 'CACHE' || Boolean(step.data?.cacheHit)).length
  const totalMs = trace.length ? trace[trace.length - 1].ms : 0
  const totalTokens = trace.reduce((acc, step) => acc + (Number(step.data?.inputTokens) || 0) + (Number(step.data?.outputTokens) || 0), 0)
  const hasError = trace.some((step) =>
    step.type === 'ERROR' ||
    step.type === 'GRAPH_CHECKPOINT_LOAD_FAILED' ||
    Boolean(step.data?.failed) ||
    (step.type === 'LANGGRAPH_NODE_EXIT' && Boolean(step.data?.error))
  )
  const isEscalated = trace.some((step) =>
    step.type === 'ESCALATE' ||
    step.type === 'ESCALATION_HANDOFF_TRIGGERED' ||
    step.type === 'ESCALATION_PROPAGATED' ||
    Boolean(step.data?.shouldEscalate)
  )
  const hitlState = useMemo(() => getHitlPendingState(trace), [trace])

  const selectedTenant = tenants.find((tenant) => tenant.id === tenantId) || null
  const selectedConversation = conversations.find((conversation) => conversation.id === conversationId) || null
  const selectedTraceSummary =
    historyTraces.find((item) => item.id === activeTraceId) ||
    historyTraces[0] ||
    null
  const selectedTraceDetail = selectedTraceSummary
    ? { ...selectedTraceSummary, steps: trace }
    : null

  const copy = async () => {
    try {
      const payload = buildTraceDebugBundle({
        tenant: selectedTenant,
        conversation: selectedConversation,
        trace: selectedTraceDetail as any,
        history: historyTraces,
        liveMode,
        activeTraceId,
      })

      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2))
      setCopyStatus('Debug bundle copiado')
      setTimeout(() => setCopyStatus(''), 2000)
    } catch {
      setCopyStatus('No se pudo copiar')
      setTimeout(() => setCopyStatus(''), 2000)
    }
  }

  const trajectoryBadgeStyle = {
    fontSize: 9,
    padding: '1px 4px',
    borderRadius: 3,
    background: 'rgba(14,165,233,0.12)',
    color: '#7dd3fc',
  } as const

  const playTrace = () => {
    if (liveMode || trace.length === 0) return
    if (playbackRef.current) clearTimeout(playbackRef.current)
    setVisibleIndexes([])
    setPlayhead(0)
    setActive(null)
    setPlaying(true)
  }

  const showAll = () => {
    if (liveMode) return
    if (playbackRef.current) clearTimeout(playbackRef.current)
    setPlaying(false)
    setVisibleIndexes(trace.map((_, index) => index))
    setPlayhead(trace.length)
  }

  const resetTrace = () => {
    if (playbackRef.current) clearTimeout(playbackRef.current)
    setPlaying(false)
    setPlayhead(0)
    setVisibleIndexes(liveMode ? trace.map((_, index) => index) : [])
    setActive(null)
  }

  const sendHitlResume = async () => {
    if (!tenantId || !conversationId) return

    const editedMessage = hitlEditedMessage.trim()
    const reason = hitlReason.trim()
    if (hitlAction === 'edit' && !editedMessage) {
      setHitlError('Debes escribir el mensaje editado para la accion edit.')
      return
    }

    setHitlSubmitting(true)
    setHitlError('')
    setHitlStatus('')

    try {
      const payload: HitlResumePayload = {
        tenantId,
        conversationId,
        resume: {
          action: hitlAction,
          ...(hitlAction === 'edit' && editedMessage ? { message: editedMessage } : {}),
          ...(reason ? { reason } : {}),
        },
      }

      const response = await traceService.resumeHitl(payload)
      const graphResult = response.data.result
      setHitlStatus(
        graphResult.success
          ? graphResult.hitlPaused
            ? 'El flujo sigue en pausa HITL.'
            : 'Resume enviado correctamente.'
          : `Fallo al ejecutar resume: ${graphResult.error || 'error desconocido'}`
      )
      setHitlPendingResponse(graphResult.pendingResponse || '')

      const traceList = await traceService.getTracesByConversation(conversationId, { tenantId, limit: 40, page: 1 })
      setHistoryTraces(traceList.data || [])
    } catch {
      setHitlError('No se pudo enviar el resume HITL')
    } finally {
      setHitlSubmitting(false)
    }
  }

  const handleAuditConversation = async () => {
    if (!conversationId || !tenantId) return
    setIsAuditing(true)
    setAuditError(null)

    const traceMessages = trace
      .filter((step) => {
        const data = (step.data || {}) as Record<string, any>
        return (
          step.type === 'USER_MESSAGE' ||
          step.type === 'AGENT_RESPONSE' ||
          Boolean(data.userMessage) ||
          Boolean(data.reply) ||
          Boolean(data.botReply) ||
          Boolean(data.message)
        )
      })
      .map((step) => {
        const data = (step.data || {}) as Record<string, any>
        const isUser = step.type === 'USER_MESSAGE' || Boolean(data.userMessage)
        const content = String(data.userMessage || data.reply || data.botReply || data.message || '')
        return {
          role: (isUser ? 'user' : 'assistant') as 'user' | 'assistant',
          content,
          timestamp: new Date().toISOString(),
        }
      })
      .filter((m) => m.content.trim().length > 0)

    try {
      const res = await traceService.evaluateConversation({
        conversationId,
        tenantId,
        deepEvaluation: true,
        messages: traceMessages.length > 0 ? traceMessages : undefined,
      })
      setAuditResult(res.data)
      setIsAuditModalOpen(true)
    } catch (err: any) {
      setAuditError(err.message || 'Error al auditar la conversación')
    } finally {
      setIsAuditing(false)
    }
  }

  return (
    <div style={{ fontFamily: "'IBM Plex Mono', monospace", background: '#0a0a0f', minHeight: '100vh', color: '#e2e8f0', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.02)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/admin" style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#cbd5e1', textDecoration: 'none', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            VOLVER
          </Link>
          <Link to="/admin/debug/observability" style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#86efac', textDecoration: 'none', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.28)' }}>
            OBSERVABILIDAD
          </Link>
          <Link to={`/admin/debug/simulator${tenantId ? `?tenantId=${tenantId}` : ''}`} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#f472b6', textDecoration: 'none', background: 'rgba(236,72,153,0.12)', border: '1px solid rgba(236,72,153,0.28)' }}>
            SIMULADOR
          </Link>
          <a href="#ai-analysis" style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#7dd3fc', textDecoration: 'none', background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.26)' }}>
            AI ANALYSIS
          </a>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '0.05em' }}>FLOW DEBUGGER</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <select value={tenantId} onChange={(event) => setTenantId(event.target.value)} disabled={loading || tenants.length === 0} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#e2e8f0', borderRadius: 8, padding: '6px 12px', fontSize: 12, minWidth: 380 }}>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id} style={{ background: '#0a0a0f' }}>
                {tenant.name} · {tenant.id}
              </option>
            ))}
          </select>
          {liveMode && (
            <div style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22c55e', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.5s infinite' }} />
              LIVE
            </div>
          )}
          <div style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, background: hasError ? '#dc262622' : isEscalated ? '#ef444422' : '#22c55e22', border: `1px solid ${hasError ? '#dc2626' : isEscalated ? '#ef4444' : '#22c55e'}`, color: hasError ? '#dc2626' : isEscalated ? '#ef4444' : '#22c55e', fontWeight: 700 }}>
            {hasError ? 'ERROR' : isEscalated ? 'ESCALATED' : 'OK'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', height: 'calc(100vh - 57px)' }}>
        <div style={{ width: 300, borderRight: '1px solid rgba(255,255,255,0.07)', overflowY: 'auto', padding: '12px 0', background: 'rgba(255,255,255,0.01)', flexShrink: 0 }}>
          <div style={{ padding: '0 14px 8px', fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Conversaciones</div>
          {conversations.map((conversation) => (
            <button
              type="button"
              key={conversation.id}
              onClick={() => setConversationId(conversation.id)}
              style={{
                width: '100%',
                textAlign: 'left',
                border: 'none',
                display: 'block',
                padding: '10px 14px',
                cursor: 'pointer',
                background: conversationId === conversation.id ? 'rgba(99,102,241,0.12)' : 'transparent',
                borderLeft: conversationId === conversation.id ? '2px solid #6366f1' : '2px solid transparent',
              }}
            >
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 3 }}>{conversation.contactName || conversation.waContact}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', lineHeight: 1.4 }}>{conversation.lastMessage || 'Sin ultimo mensaje'}</div>
              <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.32)' }}>{conversation.id}</div>
            </button>
          ))}
          {!loading && conversations.length === 0 && <div style={{ padding: '10px 14px', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>No hay conversaciones para este tenant.</div>}
        </div>

        <div style={{ width: 350, borderRight: '1px solid rgba(255,255,255,0.07)', overflowY: 'auto', padding: '12px 0', background: 'rgba(255,255,255,0.015)', flexShrink: 0 }}>
          <div style={{ padding: '0 14px 8px', fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Historial de Mensajes ({historyTraces.length})</div>
          {historyTraces.map((t) => (
            <button 
              type="button"
              key={t.id} 
              onClick={() => setActiveTraceId(t.id)} 
              style={{ 
                width: '100%',
                textAlign: 'left',
                border: 'none',
                display: 'block',
                padding: '10px 14px', 
                cursor: 'pointer', 
                background: activeTraceId === t.id ? 'rgba(99,102,241,0.1)' : 'transparent',
                borderLeft: activeTraceId === t.id ? '2px solid #6366f1' : '2px solid transparent',
                marginBottom: 1,
                transition: 'background 0.2s'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: '#6366f1', fontWeight: 700 }}>{t.strategy?.toUpperCase() || 'UNKNOWN'}</span>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>{new Date(t.createdAt).toLocaleTimeString()}</span>
              </div>
              <div style={{ fontSize: 12, color: activeTraceId === t.id ? '#fff' : 'rgba(255,255,255,0.6)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {t.message || '(Sin mensaje)'}
              </div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                 <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }}>{t.totalDurationMs}ms</span>
                 {t.shouldEscalate && <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: '#ef444422', color: '#ef4444' }}>ESCALATED</span>}
                 {t.intentTrajectory?.patterns?.includes('browse_loop') && (
                  <span style={trajectoryBadgeStyle}>BROWSE LOOP</span>
                 )}
                 {t.intentTrajectory?.patterns?.includes('purchase_oscillation') && (
                  <span style={{ ...trajectoryBadgeStyle, background: 'rgba(245,158,11,0.14)', color: '#fbbf24' }}>PURCHASE OSC</span>
                 )}
              </div>
            </button>
          ))}
          {!loadingTrace && conversationId && historyTraces.length === 0 && <div style={{ padding: '10px 14px', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>No hay historial para esta conversación.</div>}
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.01)' }}>
            <button onClick={playTrace} disabled={liveMode || trace.length === 0 || loadingTrace} style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: liveMode || trace.length === 0 || loadingTrace ? 'not-allowed' : 'pointer', background: playing ? 'rgba(99,102,241,0.22)' : '#6366f1', color: '#fff', border: 'none', opacity: liveMode || trace.length === 0 || loadingTrace ? 0.3 : 1 }}>
              {playing ? 'PLAYING...' : 'PLAY'}
            </button>
            <button onClick={showAll} disabled={liveMode || trace.length === 0 || loadingTrace} style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.06)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.1)', opacity: liveMode || trace.length === 0 || loadingTrace ? 0.3 : 1, cursor: liveMode || trace.length === 0 || loadingTrace ? 'not-allowed' : 'pointer' }}>
              SHOW ALL
            </button>
            <button onClick={resetTrace} style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.08)' }}>
              RESET
            </button>
            <button onClick={copy} disabled={trace.length === 0} style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 700, background: 'rgba(14,165,233,0.12)', color: trace.length === 0 ? 'rgba(255,255,255,0.3)' : '#7dd3fc', border: '1px solid rgba(14,165,233,0.22)', cursor: trace.length === 0 ? 'not-allowed' : 'pointer' }}>
              COPY DEBUG BUNDLE
            </button>
            <button
              onClick={handleAuditConversation}
              disabled={!conversationId || isAuditing}
              title="Auditar calidad de la conversación con IA"
              style={{
                padding: '6px 14px',
                borderRadius: 7,
                fontSize: 12,
                fontWeight: 700,
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.22), rgba(236, 72, 153, 0.22))',
                color: !conversationId || isAuditing ? 'rgba(255,255,255,0.3)' : '#f472b6',
                border: '1px solid rgba(236, 72, 153, 0.35)',
                cursor: !conversationId || isAuditing ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Bot style={{ width: 14, height: 14 }} />
              {isAuditing ? 'AUDITANDO...' : 'AUDITAR CON IA'}
            </button>
            {conversationId && (
              <Link
                to={`/admin/debug/simulator?tenantId=${tenantId}&replayConversationId=${conversationId}`}
                title="Reproducir esta conversación en el Simulador E2E"
                style={{
                  padding: '6px 14px',
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 700,
                  background: 'rgba(236, 72, 153, 0.15)',
                  color: '#f472b6',
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Play style={{ width: 14, height: 14 }} />
                REPRODUCIR EN SIMULADOR
              </Link>
            )}
            {copyStatus && <span style={{ fontSize: 11, color: copyStatus === 'JSON copiado' ? '#4ade80' : '#fca5a5' }}>{copyStatus}</span>}
            {auditError && <span style={{ fontSize: 11, color: '#fca5a5' }}>{auditError}</span>}
            <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />
            {['ALL', 'TOOLS', 'LLM', 'ERRORS'].map((item) => (
              <button key={item} onClick={() => setFilter(item)} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.06em', fontFamily: 'inherit', background: filter === item ? 'rgba(99,102,241,0.2)' : 'transparent', color: filter === item ? '#a78bfa' : 'rgba(255,255,255,0.3)', border: filter === item ? '1px solid #6366f144' : '1px solid transparent', transition: 'all 0.15s' }}>
                {item}
              </button>
            ))}
            <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />
            {[{ id: 'history', label: '📋 HISTORY' }, { id: 'live', label: '● LIVE' }].map((mode) => (
              <button key={mode.id} onClick={() => setLiveMode(mode.id === 'live')} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.06em', fontFamily: 'inherit', background: (liveMode ? mode.id === 'live' : mode.id === 'history') ? mode.id === 'live' ? 'rgba(34,197,94,0.15)' : 'rgba(99,102,241,0.2)' : 'transparent', color: (liveMode ? mode.id === 'live' : mode.id === 'history') ? mode.id === 'live' ? '#22c55e' : '#a78bfa' : 'rgba(255,255,255,0.3)', border: (liveMode ? mode.id === 'live' : mode.id === 'history') ? `1px solid ${mode.id === 'live' ? '#22c55e44' : '#6366f144'}` : '1px solid transparent', transition: 'all 0.15s' }}>
                {mode.label}
              </button>
            ))}
            <div style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{visibleIndexes.length}/{trace.length} steps · {totalMs}ms total</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <MetricsBadge label="Duracion total" value={`${totalMs}ms`} color="#22c55e" />
            <MetricsBadge label="LLM calls" value={llmCalls} color="#ec4899" />
            <MetricsBadge label="Tool calls" value={toolCalls} color="#10b981" />
            <MetricsBadge label="Cache hits" value={cacheHits} color="#f59e0b" />
            <MetricsBadge label="Tokens totales" value={totalTokens || '-'} color="#a78bfa" />
          </div>

          <DebugTraceAnalysisPanel
            tenantId={tenantId}
            conversationId={conversationId}
            trace={trace}
            historyTraces={historyTraces}
            selectedTraceSummary={selectedTraceSummary}
            selectedConversation={selectedConversation}
            activeTraceId={activeTraceId}
            liveMode={liveMode}
            loadingTrace={loadingTrace}
          />

          {hitlState.pending && (
            <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(239,68,68,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#fca5a5' }}>HITL pendiente - Operaciones Humanas</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
                  {String((hitlState.request as Record<string, unknown> | null)?.['reason'] || 'Sin motivo')}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 180px', gap: 10, alignItems: 'start' }}>
                <select
                  value={hitlAction}
                  onChange={(event) => setHitlAction(event.target.value as HitlUiAction)}
                  disabled={hitlSubmitting}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: '#e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}
                >
                  <option value="approve" style={{ background: '#0a0a0f' }}>approve</option>
                  <option value="edit" style={{ background: '#0a0a0f' }}>edit</option>
                  <option value="deny" style={{ background: '#0a0a0f' }}>deny</option>
                </select>

                <textarea
                  value={hitlAction === 'edit' ? hitlEditedMessage : hitlReason}
                  onChange={(event) => {
                    if (hitlAction === 'edit') {
                      setHitlEditedMessage(event.target.value)
                    } else {
                      setHitlReason(event.target.value)
                    }
                  }}
                  disabled={hitlSubmitting}
                  placeholder={
                    hitlAction === 'edit'
                      ? 'Mensaje final para el cliente (obligatorio en edit)'
                      : 'Razon opcional para approve/deny'
                  }
                  rows={2}
                  style={{ resize: 'vertical', minHeight: 52, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.12)', color: '#e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 12, lineHeight: 1.4 }}
                />

                <button
                  onClick={sendHitlResume}
                  disabled={hitlSubmitting}
                  style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: '1px solid rgba(34,197,94,0.35)', background: 'rgba(34,197,94,0.18)', color: '#86efac', cursor: hitlSubmitting ? 'not-allowed' : 'pointer', opacity: hitlSubmitting ? 0.5 : 1 }}
                >
                  {hitlSubmitting ? 'ENVIANDO...' : 'ENVIAR RESUME'}
                </button>
              </div>

              {hitlError && <div style={{ marginTop: 8, color: '#fca5a5', fontSize: 11 }}>{hitlError}</div>}
              {hitlStatus && <div style={{ marginTop: 8, color: '#93c5fd', fontSize: 11 }}>{hitlStatus}</div>}
              {hitlPendingResponse && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#86efac' }}>
                  pendingResponse: {hitlPendingResponse}
                </div>
              )}
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            {loadingTrace ? (
              <div style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginTop: 60, fontSize: 13 }}>Cargando trace...</div>
            ) : error ? (
              <div style={{ color: '#fca5a5', textAlign: 'center', marginTop: 60, fontSize: 13 }}>{error}</div>
            ) : filtered.length === 0 ? (
              <div style={{ color: 'rgba(255,255,255,0.2)', textAlign: 'center', marginTop: 60, fontSize: 13 }}>{liveMode ? 'Esperando mensajes en tiempo real...' : 'No hay trace para esta conversación'}</div>
            ) : (
              filtered.map((step) => {
                const originalIndex = trace.indexOf(step)
                return <StepNode key={`${step.type}-${originalIndex}-${step.ms}`} step={step} index={originalIndex} active={active === originalIndex} visible={visibleIndexes.includes(originalIndex) || liveMode} onClick={() => setActive(originalIndex)} />
              })
            )}
          </div>
        </div>

        <div style={{ width: 320, borderLeft: '1px solid rgba(255,255,255,0.07)', flexShrink: 0, overflowY: 'auto', background: 'rgba(255,255,255,0.01)' }}>
          <DetailPanel step={active === null ? null : trace[active] || null} />
        </div>
      </div>

      <TestChatEvaluationModal
        isOpen={isAuditModalOpen}
        onClose={() => setIsAuditModalOpen(false)}
        evaluation={auditResult}
        isLoading={isAuditing}
      />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1) }
          50% { opacity: .5; transform: scale(.85) }
        }
        * { box-sizing: border-box }
        ::-webkit-scrollbar { width: 4px; height: 4px }
        ::-webkit-scrollbar-track { background: transparent }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px }
        select option { background: #0a0a0f }
      `}</style>
    </div>
  )
}
